// utils/rlUuid.ts
export const RL_UUID_COOKIE = "x-rl-uuid"

// RFC4122 v4 폴백 (crypto.getRandomValues 사용)
function uuidv4Fallback(): string {
  const buf = new Uint8Array(16)
  crypto.getRandomValues(buf)
  // RFC4122: version/variant 비트 세팅
  buf[6] = (buf[6] & 0x0f) | 0x40 // version 4
  buf[8] = (buf[8] & 0x3f) | 0x80 // variant 10
  const hex = Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("")
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export function generateRlUuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID() as string
  }
  return uuidv4Fallback()
}

// ---------- Cookie helpers (client only) ----------
export type SameSite = "Lax" | "Strict" | "None"

export function setCookie(
  name: string,
  value: string,
  days = 365,
  options?: {
    domain?: string
    path?: string
    sameSite?: SameSite
    secure?: boolean // if undefined, auto: true on https, false on http
  },
) {
  const maxAge = days * 24 * 60 * 60
  const path = options?.path ?? "/"
  const sameSite = options?.sameSite ?? "Lax"

  // Auto-secure: only set Secure on HTTPS. Some browsers refuse setting Secure cookies on http:// origins (including localhost).
  let secure = options?.secure
  if (typeof secure === "undefined") {
    secure =
      typeof window !== "undefined" && window.location.protocol === "https:"
  }

  // If SameSite=None, spec requires Secure=true; force it.
  if (sameSite === "None") secure = true

  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    `Path=${path}`,
    `Max-Age=${maxAge}`,
    `SameSite=${sameSite}`,
  ]
  if (options?.domain) parts.push(`Domain=${options.domain}`)
  if (secure) parts.push("Secure")

  document.cookie = parts.join("; ")
}

export function getCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]+)`))
  return m ? decodeURIComponent(m[1]) : null
}

// ---------- Public API ----------
/**
 * 브라우저에서 x-rl-uuid를 가져오거나, 없으면 생성하여
 * Cookie에 저장한 뒤 반환.
 */
export function getOrCreateRlUuid(): string {
  if (typeof window === "undefined") {
    // SSR 방어: 서버에선 생성하지 않음
    throw new Error("getOrCreateRlUuid must be called on the client")
  }

  // 1) cookie 확인
  const cookieVal = getCookie(RL_UUID_COOKIE)
  if (cookieVal) {
    return cookieVal
  }

  // 2) 새로 생성 후 저장
  const uuid = generateRlUuid()
  try {
    setCookie(RL_UUID_COOKIE, uuid)
  } catch {}
  return uuid
}

/** 읽기 전용(없으면 null) */
export function readRlUuid(): string | null {
  if (typeof window === "undefined") return null
  return getCookie(RL_UUID_COOKIE)
}

/** 강제 재발급(테스트/리셋용) */
export function regenerateRlUuid(): string {
  if (typeof window === "undefined") {
    throw new Error("regenerateRlUuid must be called on the client")
  }
  const uuid = generateRlUuid()
  try {
    setCookie(RL_UUID_COOKIE, uuid)
  } catch {}
  return uuid
}
